import axios from 'axios';
import { API_BASE_URL } from '../../config/api';
import { CustomField, WorkflowPreview, WorkflowSettings } from '../../types';

/**
 * The form URL's query params, for custom fields that opt in to reading them.
 * The backend only reads keys the author enabled, so nothing else is used.
 */
const getQueryParams = (): Record<string, string> => {
  try {
    return Object.fromEntries(
      Array.from(new URLSearchParams(window.location.search).entries()).slice(0, 50),
    );
  } catch {
    return {};
  }
};

export const formApi = {
  getForms: async (
    tenantId: string,
    page?: number,
    limit?: number,
    filters?: { search?: string; formType?: 'single' | 'multistep'; sort?: string },
  ) => {
    const params: Record<string, string | number> = {};
    if (page) params.page = page;
    if (limit) params.limit = limit;
    if (filters?.search?.trim()) params.search = filters.search.trim();
    if (filters?.formType) params.formType = filters.formType;
    if (filters?.sort) params.sort = filters.sort;
    const response = await axios.get(`${API_BASE_URL}/tenant/${tenantId}/form`, { params });
    return response.data;
  },

  getForm: async (tenantId: string, formId: string) => {
    const response = await axios.get(`${API_BASE_URL}/tenant/${tenantId}/form/${formId}`);
    return response.data;
  },

  createForm: async (tenantId: string, data: { name?: string; status?: string; targetDevice?: string; formType?: string; data?: any }) => {
    try {
      const response = await axios.post(
        `${API_BASE_URL}/tenant/${tenantId}/form`,
        data
      );
      return response.data;
    } catch (error: any) {
      throw error;
    }
  },

  updateForm: async (tenantId: string, formId: string, data: { name?: string; status?: string; targetDevice?: string; formType?: string; data?: any; theme?: any; workflowSettings?: WorkflowSettings | null; customFields?: CustomField[] }) => {
    try {
      // Saving creates any payment plans defined in the form on the gateways, under
      // the builder URL's CRM tenant (the form's own tenant when absent).
      const crmTenantId = new URLSearchParams(window.location.search).get('crmTenantId') || undefined;
      const response = await axios.put(
        `${API_BASE_URL}/tenant/${tenantId}/form/${formId}`,
        data.data && typeof data.data === 'object' ? { ...data, crmTenantId } : data
      );
      const syncErrors: { planId: string; provider: string; message: string }[] =
        response.data?.paymentSyncErrors || [];
      if (syncErrors.length) {
        const first = syncErrors[0];
        const syncError: any = new Error(
          `Form saved, but a payment plan could not be created on ${first.provider}: ${first.message}`,
        );
        syncError.isPaymentSync = true;
        throw syncError;
      }
      return response.data;
    } catch (error: any) {
      if (error.response?.data?.error) {
        throw new Error(error.response.data.error);
      }
      throw error;
    }
  },

  deleteForm: async (tenantId: string, formId: string) => {
    const response = await axios.delete(`${API_BASE_URL}/tenant/${tenantId}/form/${formId}`);
    return response.data;
  },

  getFormData: async (tenantId: string, formId: string) => {
    const response = await axios.get(`${API_BASE_URL}/tenant/${tenantId}/form/${formId}/data`);
    return response.data;
  },

  saveFormData: async (tenantId: string, formId: string, formData: any) => {
    const response = await axios.post(
      `${API_BASE_URL}/tenant/${tenantId}/form/${formId}/data`,
      formData
    );
    return response.data;
  },
  getFormResponses: async (tenantId: string, formId: string, page?: number, limit?: number) => {
    const params: Record<string, number> = {};
    if (page) params.page = page;
    if (limit) params.limit = limit;
    const response = await axios.get(`${API_BASE_URL}/tenant/${tenantId}/form/${formId}/responses`, { params });
    return response.data;
  },

  getAllResponses: async (tenantId: string) => {
    const response = await axios.get(`${API_BASE_URL}/tenant/${tenantId}/form-responses`);
    return response.data;
  },

  getResponse: async (responseId: string) => {
    const response = await axios.get(`${API_BASE_URL}/form-response/${responseId}`);
    return response.data;
  },

  submitFormResponse: async (
    tenantId: string,
    formId: string,
    data: any,
    payment?: any,
    kyc?: any,
    esign?: any,
    responseId?: string,
  ) => {
    const queryParams = getQueryParams();
    const response = await axios.post(
      `${API_BASE_URL}/tenant/${tenantId}/form/${formId}/submit`,
      {
        data,
        ...(responseId ? { responseId } : {}),
        ...(Object.keys(queryParams).length ? { queryParams } : {}),
        ...(payment ? { payment } : {}),
        ...(kyc ? { kyc } : {}),
        ...(esign ? { esign } : {}),
      }
    );
    return response.data;
  },

  submitForPayment: async (
    tenantId: string,
    formId: string,
    data: any,
    lead: { firstName: string; lastName?: string; email: string; phone: string; state: string },
  ) => {
    // The backend creates the lead in the tenant the form's Payment / KYC / eSign
    // setting points at; the URL params let a "custom field from URL" tenant resolve.
    const queryParams = getQueryParams();
    const response = await axios.post(
      `${API_BASE_URL}/tenant/${tenantId}/form/${formId}/submit-for-payment`,
      { data, lead, ...(Object.keys(queryParams).length ? { queryParams } : {}) },
    );
    return response.data;
  },

  /**
   * What creating the workflow would do for the answers entered so far. Free.
   * The backend applies the custom fields / URL values the same way submit does.
   */
  workflowPreview: async (
    tenantId: string,
    formId: string,
    data: Record<string, any>,
  ): Promise<WorkflowPreview> => {
    const queryParams = getQueryParams();
    const response = await axios.post(
      `${API_BASE_URL}/tenant/${tenantId}/form/${formId}/workflow-preview`,
      { data, ...(Object.keys(queryParams).length ? { queryParams } : {}) },
    );
    return response.data;
  },

  /** A sample of the AI-written text (a real generation, so it is rate limited). */
  workflowSample: async (
    tenantId: string,
    formId: string,
    data: Record<string, any>,
  ): Promise<{ text: string; cached?: boolean }> => {
    const queryParams = getQueryParams();
    const response = await axios.post(
      `${API_BASE_URL}/tenant/${tenantId}/form/${formId}/workflow-preview/sample`,
      { data, ...(Object.keys(queryParams).length ? { queryParams } : {}) },
    );
    return response.data;
  },

  /** Re-runs workflow creation for a response whose workflow was not created. */
  retryWorkflow: async (tenantId: string, formId: string, responseId: string) => {
    const response = await axios.post(
      `${API_BASE_URL}/tenant/${tenantId}/form/${formId}/response/${responseId}/retry-workflow`,
    );
    return response.data;
  },

  recordPaymentOrder: async (
    tenantId: string,
    formId: string,
    responseId: string,
    payment: any,
  ) => {
    const response = await axios.patch(
      `${API_BASE_URL}/tenant/${tenantId}/form/${formId}/response/${responseId}/payment`,
      { payment },
    );
    return response.data;
  },

  deleteResponse: async (responseId: string) => {
    const response = await axios.delete(`${API_BASE_URL}/form-response/${responseId}`);
    return response.data;
  },
};
